"use client";

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

import Icon from '@/components/ui/AppIcon';

const floatingCards = [
  {
    id: 1,
    icon: '🏠',
    title: '日用品',
    subtitle: 'Daily Necessities',
    stat: '2,000+ SKU',
    color: '#F5EFE6',
    border: '#E8DDD0',
    rotate: '-3deg',
    top: '18%',
    left: '2%',
    animClass: 'float-card-1',
  },
  {
    id: 2,
    icon: '👶',
    title: '母嬰用品',
    subtitle: 'Maternal & Baby',
    stat: '800+ SKU',
    color: '#FEF3E8',
    border: '#F0DFC0',
    rotate: '2deg',
    top: '12%',
    right: '3%',
    animClass: 'float-card-2',
  },
  {
    id: 3,
    icon: '🔌',
    title: '小家電',
    subtitle: 'Small Appliances',
    stat: '500+ SKU',
    color: '#F0EBF8',
    border: '#DDD0F0',
    rotate: '-2deg',
    bottom: '22%',
    left: '1%',
    animClass: 'float-card-3',
  },
  {
    id: 4,
    icon: '🍳',
    title: '廚房用具',
    subtitle: 'Kitchen Tools',
    stat: '1,200+ SKU',
    color: '#EBF5EB',
    border: '#C8E0C8',
    rotate: '3deg',
    bottom: '18%',
    right: '2%',
    animClass: 'float-card-4',
  },
];

export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;
      const { clientX, clientY, currentTarget } = e;
      const target = currentTarget as HTMLElement;
      const rect = target.getBoundingClientRect();
      const x = (clientX - rect.left) / rect.width - 0.5;
      const y = (clientY - rect.top) / rect.height - 0.5;

      const cards = heroRef.current.querySelectorAll('.parallax-card');
      cards.forEach((card, i) => {
        const depth = (i % 2 === 0 ? 1 : -1) * (i + 1) * 6;
        (card as HTMLElement).style.transform = `translate(${x * depth}px, ${y * depth * 0.6}px) rotate(${floatingCards[i]?.rotate || '0deg'})`;
      });
    };

    const el = heroRef.current;
    el?.addEventListener('mousemove', handleMouseMove);
    return () => el?.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden grid-bg noise-overlay"
      style={{ background: 'linear-gradient(135deg, #FDFAF6 0%, #F5EFE6 50%, #EDE4D8 100%)' }}
    >
      {/* Background blobs */}
      <div
        className="absolute w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(196,145,90,0.12) 0%, transparent 70%)',
          top: '-10%',
          right: '-5%',
        }}
      />
      <div
        className="absolute w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(139,94,60,0.08) 0%, transparent 70%)',
          bottom: '5%',
          left: '-5%',
        }}
      />

      {/* Floating Cards */}
      {floatingCards.map((card, i) => (
        <div
          key={card.id}
          className={`parallax-card absolute hidden lg:flex flex-col gap-1 p-4 rounded-2xl border shadow-float z-10 cursor-default select-none ${card.animClass}`}
          style={{
            background: card.color,
            borderColor: card.border,
            top: card.top,
            left: card.left,
            right: card.right,
            bottom: card.bottom,
            transform: `rotate(${card.rotate})`,
            minWidth: '160px',
            transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
            boxShadow: '0 8px 32px rgba(44, 26, 14, 0.10)',
          }}
        >
          <span className="text-2xl">{card.icon}</span>
          <p className="font-heading font-semibold text-brand-fg text-base">{card.title}</p>
          <p className="text-xs text-brand-muted font-body">{card.subtitle}</p>
          <div
            className="mt-1 px-2 py-1 rounded-full text-xs font-semibold text-primary"
            style={{ background: 'rgba(139,94,60,0.10)' }}
          >
            {card.stat}
          </div>
        </div>
      ))}

      {/* Main content */}
      <div className="relative z-20 text-center px-6 max-w-4xl mx-auto pt-24 pb-16">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/20 bg-white/60 backdrop-blur-sm mb-8 text-sm text-primary font-medium">
          <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
          上海工廠直供 · 品質保證
        </div>

        {/* Main headline */}
        <h1
          className="font-heading font-black text-brand-fg mb-2 leading-none tracking-tight"
          style={{ fontSize: 'clamp(3rem, 10vw, 7.5rem)' }}
        >
          信悅日用品
        </h1>
        <p
          className="font-heading font-light text-primary italic mb-4"
          style={{ fontSize: 'clamp(1.2rem, 3vw, 2.5rem)' }}
        >
          XINYUEE DAILY NECESSITIES
        </p>

        <p className="text-brand-muted text-lg md:text-xl font-body max-w-2xl mx-auto mb-3 leading-relaxed">
          總部設於上海，擁有完整自有智造體系，深耕日用生活、母嬰看護、智慧小家電及高端廚居器物領域，致力為全球家庭提供高品質、設計感兼具的解決方案。現以香港作為國際化戰略支點與亞太市場門戶，全面啟動
          <strong className="text-primary font-semibold underline">線上電商平台</strong>與
          <strong className="text-primary font-semibold underline">線下專業渠道</strong>並進模式，加速構建全球化供應與品牌影響力格局。
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
          <Link
            href="/registration-center"
            className="px-8 py-4 bg-primary text-white rounded-full font-semibold text-base shadow-lg hover:bg-primary-dark hover:-translate-y-1 transition-all duration-200 flex items-center gap-2"
          >
            立即報名合作
            <Icon name="ArrowRightIcon" size={18} />
          </Link>
          <Link
            href="/business-scope"
            className="px-8 py-4 border-2 border-primary/30 text-primary rounded-full font-semibold text-base hover:bg-secondary hover:-translate-y-1 transition-all duration-200 flex items-center gap-2"
          >
            了解業務範圍
            <Icon name="ChevronRightIcon" size={18} />
          </Link>
        </div>

        {/* Quick stats */}
        <div className="flex flex-wrap items-center justify-center gap-8 mt-12 pt-8 border-t border-primary/10">
          {[
            { num: '4,500+', label: 'SKU品類' },
            { num: '500+', label: '合作夥伴' },
            { num: '2', label: '業務渠道' },
            { num: '上海', label: '工廠直供' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="font-heading font-bold text-primary text-2xl">{stat.num}</p>
              <p className="text-xs text-brand-muted mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-brand-muted animate-bounce">
        <span className="text-xs">向下滾動</span>
        <Icon name="ChevronDownIcon" size={20} />
      </div>
    </section>
  );
}